import { collection, getDocs } from 'firebase/firestore';
import { db } from '../components/firebase-config';

export interface CarouselLogo {
  id: string;
  name?: string;
  logoUrl: string;
  order?: number;
  active?: boolean;
  qrCodeUrl?: string;
  centerImageUrl?: string;
  centerLogoUrl?: string;
  brandLogoUrl?: string;
  wheelLogoScale?: number;
  wheelBackgroundColor?: string;
  wheelBackgroundColor2?: string;
  displayWidth?: number;
  displayHeight?: number;
}

const defaultLogoSources: string[] = [
  require('../assets/1.png'),
  require('../assets/2.png'),
  require('../assets/3.png'),
  require('../assets/4.png'),
  require('../assets/5.png'),
  require('../assets/7.png'),
  require('../assets/6.png'),
];

const readString = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const readNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const getRemoteLogoUrl = (record: Record<string, unknown>) =>
  readString(record.logoUrl) ||
  readString(record.logo) ||
  readString(record.imageUrl) ||
  readString(record.brandLogoUrl) ||
  readString(record.brandLogo);

export const getCarouselCenterImageUrl = (
  logo: Partial<CarouselLogo> | null | undefined,
  fallback = ''
) =>
  readString(logo?.qrCodeUrl) ||
  readString(logo?.centerImageUrl) ||
  readString(logo?.centerLogoUrl) ||
  readString(logo?.brandLogoUrl) ||
  readString(logo?.logoUrl) ||
  fallback;

export const getDefaultCarouselLogos = (): CarouselLogo[] =>
  defaultLogoSources.map((logoUrl, index) => ({
    id: `default-${index}`,
    name: `logo-${index + 1}`,
    logoUrl,
    order: index,
    active: true,
  }));

const mapLogoCollection = (
  docs: Awaited<ReturnType<typeof getDocs>>['docs']
): CarouselLogo[] =>
  docs
    .map((docSnap) => {
      const record = docSnap.data() as Record<string, unknown>;
      const logoUrl = getRemoteLogoUrl(record);

      return {
        id: docSnap.id,
        name: readString(record.name) || readString(record.brandName) || docSnap.id,
        logoUrl,
        order: readNumber(record.order),
        active: typeof record.active === 'boolean' ? record.active : true,
        qrCodeUrl:
          readString(record.qrCodeUrl) ||
          readString(record.qrCode) ||
          readString(record.qrUrl),
        centerImageUrl: readString(record.centerImageUrl),
        centerLogoUrl: readString(record.centerLogoUrl),
        brandLogoUrl:
          readString(record.brandLogoUrl) ||
          readString(record.brandLogo) ||
          readString(record.logo),
        wheelLogoScale: readNumber(record.wheelLogoScale),
        wheelBackgroundColor:
          readString(record.wheelBackgroundColor) ||
          readString(record.backgroundColor) ||
          readString(record.brandColor),
        wheelBackgroundColor2:
          readString(record.wheelBackgroundColor2) ||
          readString(record.backgroundColor2) ||
          readString(record.brandColor2),
        displayWidth: readNumber(record.displayWidth),
        displayHeight: readNumber(record.displayHeight),
      } as CarouselLogo;
    })
    .filter((item) => item.active !== false && Boolean(item.logoUrl))
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
    );

export const fetchCarouselLogos = async (): Promise<CarouselLogo[]> => {
  const querySnapshot = await getDocs(collection(db, 'carouselLogos'));
  return mapLogoCollection(querySnapshot.docs);
};

export const getDefaultHomeWheelLogos = () => getDefaultCarouselLogos();

export const fetchHomeWheelLogos = async (): Promise<CarouselLogo[]> => {
  const querySnapshot = await getDocs(collection(db, 'homeWheelLogos'));
  return mapLogoCollection(querySnapshot.docs);
};
